import { Dispatch, SetStateAction, useMemo } from 'react'

import { MasterOrderBy, MasterSearchCondition } from '@/common/api/modules/master'
import { ListFollowTableHeaderProps, useListScrollFollow } from '@/common/app/list'
import { MasterType, W2MasterData, W2Product } from '@/common/model/master/w2Master'

import MasterListBulkOperationButton from '@/components/container/modules/master/master/table/MasterListBulkOperationButton'
import MasterListRow from '@/components/container/modules/master/master/table/MasterListRow'
import MasterListThead from '@/components/container/modules/master/master/table/MasterListThead'
import W2ProductListRow from '@/components/container/modules/master/master/table/product/W2ProductListRow'
import W2ProductListThead, {
  W2_PRODUCT_TABLE_COLUMN_LENGTH
} from '@/components/container/modules/master/master/table/product/W2ProductListThead'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

interface TheadProps extends ListFollowTableHeaderProps {
  selected: boolean
  allSelected: boolean
  onCheck(checked: boolean): void
  orderBy: MasterOrderBy | null
  onChangeOrderBy(orderBy: MasterOrderBy | null): void
  float?: boolean
}

const Thead = (props: TheadProps & { masterType: MasterType }) => {
  switch (props.masterType) {
    case MasterType.W2_PRODUCT:
      return <W2ProductListThead {...props} />
    default:
      return <MasterListThead {...props} />
  }
}

interface ListRowProps {
  item: W2MasterData
  selected: boolean
  onSelect(): void
  isScrolled: boolean
}

const ListRow = (props: ListRowProps & { masterType: MasterType }) => {
  switch (props.masterType) {
    case MasterType.W2_PRODUCT:
      return <W2ProductListRow {...props} item={props.item} />
    default:
      return <MasterListRow {...props} item={props.item} />
  }
}

const tableLength = (masterType: MasterType): number => {
  switch (masterType) {
    case MasterType.W2_PRODUCT:
    default:
      return W2_PRODUCT_TABLE_COLUMN_LENGTH
  }
}

interface Props {
  siteId: number
  masterType: MasterType
  items: W2MasterData[]
  reload(): void
  selectedIds: number[]
  onSelect(id: number): void
  onSelectAll(checked: boolean): void
  condition: MasterSearchCondition
  setCondition: Dispatch<SetStateAction<MasterSearchCondition>>
  onUpdatePage: (page: number) => void
  total: number
}

const MasterListTable = ({
  siteId,
  masterType,
  items,
  reload,
  selectedIds,
  onSelect,
  onSelectAll,
  condition,
  setCondition,
  onUpdatePage,
  total
}: Props) => {
  const allSelected = useMemo(() => {
    return items.every((item) => selectedIds.includes(item.id))
  }, [items, selectedIds])

  const listFollowProps = useListScrollFollow(tableLength(masterType))
  const { headerFloat, header } = listFollowProps

  const theadProps: TheadProps = {
    onCheck: onSelectAll,
    selected: selectedIds.length > 0,
    allSelected: allSelected,
    orderBy: condition.orderBy,
    onChangeOrderBy: (orderBy: MasterOrderBy | null) => {
      setCondition((condition) => ({ ...condition, page: 1, orderBy }))
    },
    ...header
  }

  return (
    <ScrollFollowTable
      listFollowProps={listFollowProps}
      tableHead={<Thead {...theadProps} masterType={masterType} />}
      tableFloatHead={<Thead {...theadProps} float masterType={masterType} />}
      body={
        <>
          {items.map((item) => (
            <ListRow
              key={item.id}
              masterType={masterType}
              item={item}
              selected={selectedIds.includes(item.id)}
              onSelect={() => onSelect(item.id)}
              isScrolled={header.isScrolled}
            />
          ))}
        </>
      }
      condition={condition}
      onUpdatePage={onUpdatePage}
      total={total}
      selectedIdsQuantity={selectedIds.length}
      selectedNode={
        <MasterListBulkOperationButton
          siteId={siteId}
          masterType={masterType}
          selectedIds={selectedIds}
          reload={reload}
        />
      }
    />
  )
}

export default MasterListTable
